import React, {
  FC,
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { getOperatorPickedStation } from "Slices/operatorStation";
import {
  getCurrentMissionAsset,
  getIsMissionAssetsEmpty,
} from "Slices/operatorMissionAssets";
import { fetchDeployMissionPlan } from "Slices/missionPlans";
import {
  getIsPickedFleetHasVehicles,
  getMissionOperatorPickedFleetId,
  getOperatorMissionStatus,
} from "Slices/operatorData";
import { FMSLogger } from "FMSLogger";
import { MISSION_OPERATOR_ACTIONS } from "Utils/constants";

import {
  InteractionPaneWrapper,
  CallVehicleButtonStyled,
  EmptyErrorStyled,
} from "./styles";
import { ReactComponent as HornIcon } from "../../assets/horn-icon.svg";
import { SelectedStation } from "../SelectedStation";
import {
  getIsMissionAssetsLoaded,
  getIsMissionAssetsLoadFailed,
} from "Slices/operatorMissionAssets/selectors";

const missionOperatorLogger = FMSLogger.byPrefix(MISSION_OPERATOR_ACTIONS);

type InteractionPaneProps = {
  isSelectStationModalOpen: boolean;
  setSelectStationModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

export const StationPicker: FC<InteractionPaneProps> = memo(
  ({ isSelectStationModalOpen, setSelectStationModalVisible }) => {
    const dispatch = useDispatch();
    const [isDeploying, setIsDeploying] = useState(false);
    const deployTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const pickedStation = useSelector(getOperatorPickedStation);
    const currentMissionAsset = useSelector(getCurrentMissionAsset);
    const isMissionAssetsLoaded = useSelector(getIsMissionAssetsLoaded);
    const isMissionAssetsLoadFailed = useSelector(getIsMissionAssetsLoadFailed);
    const isMissionAssetsEmpty = useSelector(getIsMissionAssetsEmpty);
    const missionOperatorPickedFleetId = useSelector(
      getMissionOperatorPickedFleetId
    );
    const operatorMissionStatus = useSelector(getOperatorMissionStatus);
    const isPickedFleetHasVehicles = useSelector(getIsPickedFleetHasVehicles);

    // Reset isDeploying when operatorMissionStatus changes from NO_MISSION to something else
    // This indicates that the deployment was successful and a mission is now in progress
    useEffect(() => {
      if (operatorMissionStatus !== "NO_MISSION") {
        setIsDeploying(false);
        if (deployTimeoutRef.current) {
          clearTimeout(deployTimeoutRef.current);
          deployTimeoutRef.current = null;
        }
      }
    }, [operatorMissionStatus]);

    // Cleanup timeout on component unmount
    useEffect(() => {
      return () => {
        if (deployTimeoutRef.current) {
          clearTimeout(deployTimeoutRef.current);
        }
      };
    }, []);

    const isCallVehicleButtonDisabled =
      !currentMissionAsset ||
      !missionOperatorPickedFleetId ||
      operatorMissionStatus !== "NO_MISSION" ||
      !isPickedFleetHasVehicles ||
      !pickedStation ||
      isDeploying;

    const handleDeployMissionAsset = useCallback(() => {
      missionOperatorLogger.debug("Call Vehicle button pressed");
      if (!currentMissionAsset || !missionOperatorPickedFleetId) {
        missionOperatorLogger.error(
          "No currentMissionAsset or missionOperatorPickedFleetId when Call Vehicle button pressed"
        );
        return;
      }

      if (!pickedStation) {
        missionOperatorLogger.error(
          "No station picked when Call Vehicle button pressed"
        );
        return;
      }

      // Set isDeploying to true immediately to disable the button
      setIsDeploying(true);

      // Set a timeout to reset the button state in case of deployment failure
      // This prevents the button from being permanently disabled
      deployTimeoutRef.current = setTimeout(() => {
        setIsDeploying(false);
        missionOperatorLogger.warn(
          "Deployment timeout - resetting button state"
        );
      }, 10000); // 10 seconds timeout

      dispatch(
        fetchDeployMissionPlan({
          ...currentMissionAsset,
          fleet: missionOperatorPickedFleetId,
          stationId: pickedStation.stationId,
        })
      );
    }, [currentMissionAsset, missionOperatorPickedFleetId, pickedStation]);

    const ErrorMessage = useMemo(() => {
      if (isMissionAssetsEmpty || isMissionAssetsLoadFailed)
        return <EmptyErrorStyled>Error: no mission plans!</EmptyErrorStyled>;

      if (!currentMissionAsset && isMissionAssetsLoaded)
        return (
          <EmptyErrorStyled>
            Error: No appropriate mission plan!
          </EmptyErrorStyled>
        );
      return null;
    }, [
      isMissionAssetsEmpty,
      currentMissionAsset,
      isMissionAssetsLoaded,
      isMissionAssetsLoadFailed,
    ]);

    return (
      <InteractionPaneWrapper>
        <SelectedStation
          pickedStation={pickedStation}
          isSelectStationModalOpen={isSelectStationModalOpen}
          setSelectStationModalVisible={setSelectStationModalVisible}
        />
        <CallVehicleButtonStyled
          disabled={isCallVehicleButtonDisabled}
          onClick={handleDeployMissionAsset}
        >
          <HornIcon />
          Call Vehicle
        </CallVehicleButtonStyled>
        {ErrorMessage}
      </InteractionPaneWrapper>
    );
  }
);
